import axios from 'axios';
import { apiClient } from './client';

// docs/plan/customer-master-ux/plan.md — 통합 시트의 frontend api client.
// 백엔드 GET /customer-master 응답을 그대로 받음.

export type CustomerMasterStatus =
  | 'undefined'
  | 'in_negotiation'
  | 'contracted'
  | 'install_ready'
  | 'install_done'
  | 'return_planned'
  | 'retired';

// 마감 차수(26-08-24) — '(지역 미지정)' 버킷 리터럴의 **콘솔 미러를 없앴다.**
// 종전 이 자리에 `export const REGION_UNASSIGNED = '(지역 미지정)'` 이 있었다. 서버 정본
// (apps/api/src/customer-master/customer-master.row.ts 의 같은 이름 상수)과 글자가 같아야
// 하는데 그 짝을 검사하는 관문이 **어디에도 없었다** — 서버 글자를 다듬으면 api 시험은
// 전부 초록인 채 사업 대시보드의 지역 클릭이 조용히 빈 목록이 됐다(마스터는 서버가 준
// row.region 과 URL 파라미터를 글자로 대조한다).
// 이제 서버가 버킷 키를 응답에 실어 준다(`InstallOverview.unassignedRegionKey`) — 화면은
// 그 값을 라벨·링크에 그대로 쓴다. 여기에 리터럴을 다시 만들지 말 것.

// S1 Phase 1 — install_ready 라벨 '설치 대기'. S1 Phase 2 — retired('종료') 추가.
export const CUSTOMER_MASTER_STATUS_LABEL: Record<CustomerMasterStatus, string> = {
  undefined: '미정',
  in_negotiation: '계약 체결중',
  contracted: '계약 완료',
  install_ready: '설치 대기',
  install_done: '설치 완료',
  return_planned: '회수 예정',
  retired: '종료',
};

// Kuun 토큰 기반 고객사 상태 chip 색상 — tokens.css 시맨틱 토큰 사용.
export const CUSTOMER_MASTER_STATUS_COLOR: Record<
  CustomerMasterStatus,
  { fg: string; bg: string; border: string }
> = {
  undefined: {
    fg: 'var(--fg-disabled)',
    bg: 'var(--bg-disabled)',
    border: 'var(--border-subtle)',
  },
  in_negotiation: {
    fg: 'var(--robot-maintenance)',
    bg: 'var(--robot-maintenance-tint)',
    border: 'var(--robot-maintenance)',
  },
  // round5 #1b — 브랜드 틸은 install_done 그린과 같은 색상군이라 구분 불가
  // → 전용 인디고 토큰(tokens.css --status-contracted)으로 분리.
  contracted: {
    fg: 'var(--status-contracted)',
    bg: 'var(--status-contracted-tint)',
    border: 'var(--status-contracted)',
  },
  install_ready: {
    fg: 'var(--dday-warning)',
    bg: 'var(--bg-warning-subtle)',
    border: 'var(--border-warning-saturated)',
  },
  install_done: {
    fg: 'var(--robot-operating)',
    bg: 'var(--bg-success-subtle)',
    border: 'var(--border-success-saturated)',
  },
  return_planned: {
    fg: 'var(--fg-danger)',
    bg: 'var(--bg-danger-subtle)',
    border: 'var(--border-danger-saturated)',
  },
  // S1 Phase 2 — 종료(회수 완료): 중립 회색.
  retired: {
    fg: 'var(--fg-subtle)',
    bg: 'var(--bg-sunken)',
    border: 'var(--border-default)',
  },
};

// 개선 9건 차수2c(26-08-04) §5 — 계약 요약 1건(서버 CustomerMasterContractSummary 와 같은 모양).
//
// 시트가 네 자리를 구분해 쓴다(서버 customer-master.row.ts 머리 주석과 같은 규칙):
//   • row.contract        = 가장 최근 계약 **그 자체**(금액 5칸까지 그 계약의 값) —
//     로봇 줄·편집 프리필. **저장이 닿는 계약.**
//   • row.soonestContract = 가장 임박한 만기 계약 — 반납일·D-day·진행 상태.
//   • row.contracts       = 전부 — 계약 카드, '계약 N건' 배지·툴팁, 갱신 임박 필터.
//   • row.totals          = 고객사 단위 합계(금액 5칸 + 대수) — 시트의 금액·대수 열.
// 계약이 1건이면 세 계약 자리가 같은 계약이고 합계도 그 한 값이다.
//
// 개선 9건 차수2e(26-08-04) ④ — 서버가 이 요약을 **계약 카드를 그대로 그릴 수 있는 한 벌**로
// 넓혔다(금액·계약별 도입일·모델 줄·예약 로봇 수). 이 미러가 뒤처지면 화면은 그 값이 이미
// 응답에 실려 있는데도 없는 줄 알고 row.contract(최신 1건)로 되돌아간다 — 그것이 계약 2건인
// 고객사의 카드에 다른 계약 금액·다른 계약 배치일이 찍힌 결함의 경로였다.
// **서버 원문(apps/api/src/customer-master/customer-master.row.ts)이 정본이다** — 칸의 뜻은
// 그 파일 주석에 있고 여기에는 옮겨 적지 않는다(두 벌이 되면 반드시 갈라진다).
export interface CustomerMasterContractSummary {
  id: string;
  humanId: string;
  status: string;
  /** 배정·설치 단계 원문(PENDING_SIGN / … / INSTALLED). 새 계약 유도 안내의 근거. */
  handoffStatus: string;
  /** 이 계약 줄들의 계약 형태 요약 — **원문 7값 기준**('PILOT 1 · LEASE_OPERATING 2'). */
  contractType: string | null;
  contractedAt: string | null;
  endDate: string | null;
  daysRemaining: number | null;
  /** 이 계약의 도입일(설치 완료). **계약 스코프** — 다른 계약 로봇의 배치일이 아니다. */
  installCompletedAt: string | null;
  durationMonths: number | null;
  rentalTermMonths: number | null;
  /** 이 계약의 보증금. null = 미입력, 0 = 명시적 0원. */
  depositKrw: number | null;
  subsidyKrw: number | null;
  referralFeeKrw: number | null;
  totalSubscriptionFeeKrw: number | null;
  totalSalesPriceKrw: number | null;
  totalContractQty: number;
  /** 이 계약에 예약(배정)된 로봇 대수. */
  reservedRobotCount: number;
  /** 이 계약의 모델별 줄. row.robots 와 **같은 타입 한 벌**이다. */
  robots: CustomerMasterRobotLine[];
  isPrimary: boolean;
  /**
   * 시트의 **계약기간·도입일·반납일·D-Day** 네 칸이 보는 계약인가(= row.soonestContract).
   * 다계약 표시 차수3(26-08-05) — 후보에서 **판매 전용 계약이 빠졌다**(판매는 소유가
   * 고객사로 넘어가 반납이 없다). 판매만 있는 고객사에서는 참인 계약이 하나도 없다 —
   * 그 이유는 row.soonestContractBasis 가 말한다.
   */
  isSoonest: boolean;
  /**
   * 시트의 **진행 상태** 칸이 보는 계약인가. 구독 계약이 있으면 isSoonest 와 같은 계약이고,
   * 판매만 있는 고객사에서만 갈린다(그때 네 칸은 '–' 이고 상태는 그 판매 계약의 단계다).
   * 구버전 서버는 이 칸을 내리지 않는다 → 옵셔널로 읽는다.
   */
  isStatusBasis?: boolean;
}

// 개선 9건 차수2f(26-08-04) — **고객사 단위 합계**가 사는 자리.
//
// 왜 새 자리인가 — 앞 차수는 시트의 합계를 `row.contract` 의 금액 5칸에 덮어썼다. 그
// 객체는 원래 "편집·저장이 닿는 계약(최신 계약) 그 자체"라는 뜻이라, 한 객체가 두 가지를
// 뜻하게 됐고 두 사고가 났다:
//   • 편집 창이 합계를 그 계약의 값으로 프리필해, 다른 계약의 보증금이 이 계약에 새로 생겼다.
//   • 합산이 계약 상태를 보지 않아 해지·만료 계약의 금액이 현재 값으로 더해졌다.
// 그래서 뜻을 두 자리로 나눴다 — row.contract 는 그 계약의 값, 합계는 여기.
//
// **시트만 이 자리를 읽는다.** 계약 카드는 contracts[](계약별 값)를 읽고, 편집 창은
// row.contract 를 읽는다. 뜻의 정본은 서버 원문(apps/api/src/customer-master/
// customer-master.row.ts)이다 — 여기 옮겨 적지 않는다(두 벌이 되면 갈라진다).
export interface CustomerMasterRowTotals {
  /** 월 구독료 합. null = 센 계약이 전부 미입력. 0 = 명시적 0원. */
  subscriptionFeeKrw: number | null;
  /** 판매가 합(판매 줄만). */
  salesPriceKrw: number | null;
  /** 보증금 합. */
  depositKrw: number | null;
  /** 지원금 합. */
  subsidyKrw: number | null;
  /** 소개비 합. */
  referralFeeKrw: number | null;
  /** 계약 대수 합. */
  contractQty: number;
  /**
   * 무엇을 세었는가 — 화면이 정직하게 말할 근거.
   *   'active'          — 유효(진행 중) 계약만 셌다.
   *   'latest-fallback' — 진행 중 계약이 하나도 없어 최신 계약 한 건만 셌다.
   */
  basis: 'active' | 'latest-fallback';
  /** 실제로 센 계약. 안내가 "어느 계약을 셌는지"까지 말하는 근거. */
  countedContractIds: string[];
  /**
   * 다계약 표시 차수3(26-08-05) — 시트 '모델' 칸. **센 계약 전부**의 모델별 대수
   * ('GEN1_D1 × 1, ZEPHYR × 1'). 줄이 없으면 ''. 무엇을 합쳤는지는 위 basis·
   * countedContractIds 가 말한다(금액·대수와 같은 계약들이다).
   * 구버전 서버는 내리지 않는다 → 옵셔널. 없으면 row.robotTypeSummary 로 폴백한다
   * (그 칸도 같은 값이다 — 서버가 별칭으로 남겼다).
   */
  robotTypeSummary?: string;
  /**
   * 다계약 표시 차수3(26-08-05) — 시트 '계약 형태' 칸의 **원문 7값 요약**
   * ('LEASE_OPERATING 1 · SALE 1'). 사람이 읽는 '구독/판매' 는 화면이 접는다(접기 정본은
   * lib/contract-reason 한 벌 — 서버는 번역하지 않는다).
   */
  contractType?: string | null;
  /**
   * 위 두 칸의 **입력** — 센 계약의 줄을 원문별 대수로 접은 것. 접기 정본이 줄 배열을
   * 받으므로 서버가 함께 내린다. 화면이 "어느 계약의 줄을 합칠지"를 스스로 판정하면
   * 판정이 두 벌이 되고 형태 칸과 금액·대수 칸이 다른 계약 집합을 말한다.
   * ⚠ 편집 프리필용이 아니다 — 저장은 계약 하나에만 닿으므로 프리필은 row.robots 다.
   */
  robotLines?: Array<{ reason: string; quantity: number }>;
}

// ============================================================
// 계획서 요구 2a §2·§3(26-08-21) — 시트 '회수일' 열이 읽는 값 (row.returnInfo)
// ============================================================
//
// **서버 원문(apps/api/src/customer-master/customer-master.row.ts)이 정본이다** — 칸의
// 뜻은 그 파일 주석에 있고 여기에는 표기에 필요한 만큼만 적는다(두 벌이 되면 갈라진다).

/** 로봇 한 대의 회수 기록 — '회수일' 칸의 누르는 안내 버튼이 나열한다(returnedAt 내림차순). */
export interface CustomerMasterReturnRecord {
  robotId: string;
  /** 로봇 식별 표기(짧은 SN). null 이면 화면이 robotId 로 폴백한다 — 지어내지 않는다. */
  serialShort: string | null;
  /** 그 배치의 회수일(ISO). */
  returnedAt: string;
}

/**
 * 시트 '회수일' 칸이 읽는 값 전부. 표기 규칙(화면 규칙 절):
 *   returnedAt(확정) → 그 날짜 / 없고 returnScheduledAt → 흐린 톤 '예정 2026.09.01' /
 *   둘 다 없으면 '–'(지어내지 않는다).
 */
export interface CustomerMasterReturnInfo {
  /** 나가 있는 로봇 수 — §1 정본(회수 안 됨 + 로봇 DEPLOYED + 로봇 미삭제, kind 필터 없음). */
  outstandingRobotCount: number;
  /**
   * 고객사 회수일(확정) — **나가 있는 로봇 0 + 기록 있음**일 때 max(records.returnedAt).
   * 일부만 회수된 고객사는 null 이다(다 회수하지 않았는데 확정이라 말하지 않는다).
   */
  returnedAt: string | null;
  /** 회수 예정일(Customer.returnScheduledAt — 필드팀이 활성 고객사 탭에서 확정하는 값). */
  returnScheduledAt: string | null;
  /**
   * 로봇별 회수 기록(returnedAt 내림차순). null = 그 응답 경로가 기록을 안 실은 것
   * (서버 내부 판정용 — **콘솔로 오는 list/get/저장 응답에서는 항상 배열**이다),
   * [] = 기록이 없다. 두 값은 다른 사실이라 화면은 null 을 []로 접지 않는다.
   */
  records: CustomerMasterReturnRecord[] | null;
}

export interface CustomerMasterRow {
  id: string;
  no: number;
  displayName: string;
  legalName: string;
  region: string;
  // round11 (요구 11) — 국가(2자리 코드). 국내/해외 탭 기준값 + 편집 prefill.
  countryCode: string;
  address: string | null;
  addressMapUrl: string | null;

  // round11 (요구 10) — 쇼잉 여부. null = 미입력.
  isShowing: boolean | null;
  hasGrillTeam: boolean | null;
  isSoleTrader: boolean | null;
  hasAJ: boolean | null;
  hasInsurance: boolean | null;
  hasGrillerSeparate: boolean | null;
  // 운영자 피드백 (round 3) — '렌탈사' free-form string. OptionGroup
  // `customer.rentalCompany` picklist. backend 가 hasAJ 도 derive 해 sync.
  rentalCompany: string | null;

  primaryContactName: string | null;
  primaryContactPosition: string | null;
  primaryContactPhone: string | null;
  primaryContactEmail: string | null;

  storeCount: number;
  // S1 Phase 5 (R2 #3) — PDF §1 '매장 수' (예상 매장 수). edit prefill 용.
  expectedStoreCount: number | null;
  segment: string;
  salesOwnerCode: string | null;
  salesRep: { id: string; displayName: string } | null;

  deal: {
    id: string;
    humanId: string;
    stage: string;
    expectedCloseAt: string | null;
  } | null;

  // FB §4c (결정 B②) — Contract 가 없을 때 '계약' 카드를 채우는 견적 기준
  // (Deal/마스터 입력값). Contract 가 있으면 null.
  dealQuote: {
    dealId: string;
    humanId: string;
    expectedRobotCount: number | null;
    expectedContractType: string | null;
    expectedContractDurationMonths: number | null;
    expectedCloseAt: string | null;
    models: Array<{ modelCode: string; modelDisplay: string; quantity: number }>;
    totalQuantity: number;
  } | null;

  // 개선 9건 차수2f(26-08-04) — 이 객체는 **최신 계약(= 편집·저장이 닿는 계약) 그 자체**다.
  // 금액 5칸·대수도 그 계약의 값이다. 편집 창이 이 값을 프리필하고 저장은 그 계약 한 건에
  // 쓰므로, 여기 고객사 단위 합계를 담으면 다른 계약의 금액이 이 계약에 새로 생긴다.
  // 고객사 단위 합계는 row.totals 다(시트만 읽는다).
  contract: {
    id: string;
    humanId: string;
    status: string;
    contractType: string | null;
    contractedAt: string | null;
    startDate: string | null;
    endDate: string | null;
    rentalTermMonths: number | null;
    subscriptionStatus: string | null;
    daysRemaining: number | null;
    unitPriceKrw: number | null;
    // 이 계약의 구독료 소계 합 — Σ(대당 월구독료 × 수량).
    totalSubscriptionFeeKrw: number | null;
    listPriceKrw: number | null;
    // 이 계약의 판매가 소계 합 — 판매(SALE) 줄만.
    totalSalesPriceKrw: number | null;
    depositKrw: number | null;
    // 개선 9건 차수2(26-08-04) §7 (결정 ④A) — 지원금·소개비(계약 단위). 서버 row 와
    // 같은 모양. 0 과 null 이 서로 다른 사실이라 화면이 접기 전까지 구분해 실려 온다
    // (표기 정본 lib/amount-or-none 이 0·null 을 모두 '–' 로 접는다).
    subsidyKrw: number | null;
    referralFeeKrw: number | null;
    totalContractQty: number;
    // round5 #2b④ — 희망 설치일(편집 모달 prefill 용).
    installScheduledAt: string | null;
    // round5 #22 — 도입일(설치 완료). InfraOrder 우선, fallback 최신 활성 배치일.
    installCompletedAt: string | null;
  } | null;

  // 개선 9건 차수2c(26-08-04) §5 — 이 고객사의 계약 전부(임박한 만기 순) + 시트 기준 계약.
  // 구버전 서버 호환을 위해 옵셔널로 읽고 화면은 `?? []` 로 폴백한다(없으면 종전처럼
  // row.contract 만 보는 동작 — 조용히 깨지지 않는다).
  contracts?: CustomerMasterContractSummary[];
  // 다계약 표시 차수3(26-08-05) — soonestContract 는 이제 **먼저 끝나는 구독 계약**이다
  // (판매 전용은 후보가 아니다 — 판매는 소유가 넘어가 반납일이 필요 없다). 시트의
  // 계약기간·도입일·반납일·D-Day 네 칸이 이것을 본다.
  // **판매만 있는 고객사는 null 이고 네 칸이 '–' 인 것이 맞다**(오너 확인). 그때
  // `?? row.contract` 로 폴백하면 판매 계약의 반납일이 다시 찍힌다 — 폴백하지 말고
  // 아래 basis 가 말하는 이유를 화면이 그대로 옮길 것.
  soonestContract?: CustomerMasterContractSummary | null;
  /**
   * 그 기준 계약을 **어떻게 골랐는가 / 왜 없는가**.
   *   'return-watchable' — 판매 전용이 아닌 계약 중 먼저 끝나는 것(정상 경로).
   *   'sale-only'        — 계약은 있지만 **전부** 판매 전용 → 지켜볼 반납일이 없다.
   *   'live-sale-only'   — 정정3(26-08-06) — **살아 있는** 계약이 전부 판매 전용이고 구독
   *                        계약은 해지됐다. 빈 칸이라는 답은 'sale-only' 와 같지만 이유가
   *                        다르다(이 고객사에는 구독 계약이 있다 — 끝났을 뿐이다).
   *   'no-contract'      — 계약이 하나도 없다.
   * 뒤 세 값은 **같은 빈 칸의 서로 다른 이유**다 — 같은 말을 하면 운영자는 계약이 누락된
   * 줄 안다. 구버전 서버는 내리지 않는다 → 옵셔널.
   * ⚠ 값이 늘면 lib/term-basis 의 문구도 늘어야 한다. 모르는 값은 그 정본이 'no-contract'
   *   쪽으로 접으므로 **반납일을 지어내지는 않지만** 빈 칸의 이유를 잘못 말한다.
   */
  soonestContractBasis?: 'return-watchable' | 'sale-only' | 'live-sale-only' | 'no-contract';
  /**
   * 다계약 표시 차수3(26-08-05) — 이 고객사의 **계약일**(전 계약 중 가장 이른 것).
   * 시트 기본 정렬('계약일 순')이 쓰는 키와 같은 값이다.
   * ⚠ 시트의 '계약일' 칸은 최신 계약 기준이라(row.contract.contractedAt) 계약이 여럿이면
   *   이 값과 다르다 — 순서를 설명해야 할 때 이 값을 쓴다. null = 계약일 미상(맨 뒤).
   */
  firstContractedAt?: string | null;

  /**
   * 계획서 요구 2a §2(26-08-21) — 시트 '회수일' 열(반납일·D-Day 바로 옆)의 입력.
   * 새 서버는 list/get/저장 응답 전부에 싣는다. **구버전 서버는 이 칸 자체가 없다** →
   * 옵셔널로 읽고, 없으면 칸을 '–' 로 두되 표 위 안내가 그 사실을 말한다
   * ("이 서버가 아직 회수일을 내리지 않습니다" — 조용한 빈칸 금지, 화면 규칙 절).
   */
  returnInfo?: CustomerMasterReturnInfo;

  // 개선 9건 차수2f(26-08-04) — 고객사 단위 합계(금액 5칸 + 계약 대수 + 무엇을 셌는가).
  // 옵셔널로 **읽기만** 한다: 이 칸을 아직 내리지 않는 서버가 있을 수 있어서다. 없을 때
  // 화면이 옛 합산으로 되돌아가지 않는다 — 옛 합산이 정확히 이번에 닫은 결함이다
  // (해지·만료 계약 금액을 현재 값으로 더했다). 못 받으면 칸을 비우고 그 사실을 말한다.
  totals?: CustomerMasterRowTotals | null;

  // 개선 9건 차수2c(26-08-04) §5 — 저장 응답에만 실리는 일회성 알림. 같은 고객사에
  // 배정 대기 계약이 2건 이상이라 이미 나가 있는 로봇의 자동 연결이 멈췄다는 사실
  // (종전엔 서버 로그에만 남아 저장한 사람이 알 수 없었다).
  assignmentLinkWarning?: string | null;

  status: CustomerMasterStatus;
  // round6 §1-2/R3 — 진행 상태 수동 고정 여부(고정 배지 + 해제 옵션 노출 근거).
  statusPinned: boolean;
  // FB §4b — '회수 예정' 사유. CustomerDetailPage 상태 영역 표시.
  returnReason: string | null;

  grillSelector: {
    type: string | null;
    installStatus: string | null;
    deliveredAt: string | null;
  };

  // 개선 9건 차수2e(26-08-04) ④ — 최신 계약(편집이 닿는 계약)의 줄. 모양은
  // CustomerMasterRobotLine 한 벌이고 계약별 contracts[].robots 도 같은 타입이다
  // (인라인으로 두면 한쪽에 칸을 더할 때 다른 쪽이 조용히 뒤처진다 — 서버가 같은 이유로
  // 이름 있는 타입 한 벌로 뽑았다).
  robots: CustomerMasterRobotLine[];
  /**
   * 모델별 대수 한 줄('GEN1_D1 × 1, ZEPHYR × 1').
   * 다계약 표시 차수3(26-08-05) — 서버가 이 값을 **totals.robotTypeSummary(전 계약 합집합)와
   * 같게** 내린다(별칭). 종전엔 최신 계약 1건의 줄만 말해서 추가 판매를 새 계약으로 등록한
   * 고객사의 모델 칸이 그 판매 계약만 보여 줬다.
   */
  robotTypeSummary: string;
  totalRobotCount: number;
  deployedRobotCount: number;

  // Phase 5 — 첨부 카운트 (고객 상세 페이지 "첨부" 섹션용)
  attachments: {
    install: number;
    selector: number;
    training: number;
  };

  profileConfig: string | null;
  notes: string | null;
  // 이슈1e — 삭제 여부(삭제됨 배지 + 복구/완전삭제 액션). null = 활성.
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerMasterRobotLine {
  // 계약 사실화 1단계 정정(26-07-28) — 이 줄의 신원(서버 ContractLine.id).
  // 편집 모달이 저장할 때 그대로 되돌려 보낸다 → 서버가 어느 줄을 고친 것인지
  // 추측하지 않는다(앞 행을 지웠을 때 남은 값이 옮겨 붙던 사고를 닫는다).
  id: string;
  modelCode: string;
  modelDisplay: string;
  quantity: number;
  // 계약 사실화 1단계 정정(26-07-28) — 이 줄의 계약 형태 **원문**(7값).
  // SALE / LEASE_OPERATING / LEASE_FINANCE / PILOT / DEMO / EVENT / INTERNAL.
  // 줄별 칸의 prefill·전송은 이 값을 쓴다 — 아래 contractType 은 2값으로 접힌
  // 표기라, 그것을 되돌려 보내면 파일럿·시연·행사·사내·금융리스가 렌탈로 덮인다.
  reason: string;
  // 계약 사실화 1단계(26-07-28) — 이 줄의 계약 형태('판매' / '렌탈').
  // 서버 ContractLine.reason 을 2값으로 **접은 표기**(목록 칸과 같은 어휘).
  // ⚠ 읽기 전용. 쓰기에는 위 reason 을 쓴다.
  contractType: '판매' | '렌탈';
  direction: string | null;
  setting: string | null;
  grillPlate: string | null;
  specialParts: string | null;
  wireGrill: string | null;
  // Phase 5 — per-line 가격 (multi-model 시 정확)
  unitPriceKrw: number | null;
  listPriceKrw: number | null;
  // S1 Phase 5 (R2 #3) — per-line Store ID / 희망 색상 / 희망 불감지. edit prefill 용.
  // S2 Phase 2.5 — + 희망 방향.
  storeId: string | null;
  desiredColor: string | null;
  desiredSensor: string | null;
  desiredDirection: string | null;
}

export interface ListCustomerMasterResponse {
  items: CustomerMasterRow[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * 다계약 표시 차수3 정정5(26-08-06) — 목록 조회의 **답 + 한 가지 사정**.
 *
 * 🔴 왜 생겼나(배포 구간 사고) — 이 차수의 콘솔은 기본 정렬로 `sort=contractedAt` 를 **명시로**
 *   보낸다. 운영 api 의 DTO 는 아직 `@IsEnum(['recent','name'])` 이고 main.ts 의 ValidationPipe 가
 *   `forbidNonWhitelisted` 라, 그 값을 받으면 **400 'invalid sort'** 를 낸다. 프런트와 api 를
 *   따로 배포하는 지금 런북에서 **콘솔이 먼저 올라간 구간은 반드시 생기고**(2026-08-05 배포도
 *   콘솔 → api 순서였다), 데스크톱 설치본은 그 구간보다 오래 남는다. 그 사이 고객사 마스터
 *   시트는 두 탭·전 역할·전 페이지가 **행 0개**가 된다(정렬 값이 filters 초기값이라 검색·필터로
 *   우회할 수도 없다).
 *
 * 어떻게 닫는가 — 이 차수의 다른 새 칸(soonestContractBasis · totals.* · firstContractedAt)을
 * 전부 옵셔널로 **읽는** 것과 **같은 규칙**을 보내는 쪽에도 적용한다: 정렬 값 하나 때문에
 * 화면이 통째로 비지 않게, 400 을 만나면 `sort` 를 빼고 **한 번만** 다시 받는다.
 *   · 재시도가 성공하면 원인이 정렬이었다는 뜻이다 — 그 사실을 `rejectedSort` 로 **말한다**
 *     (조용히 삼키면 화면이 자기가 고른 순서라고 거짓말한다. 서버 기본 순서는 화면이 고른
 *     순서가 아니다).
 *   · 재시도도 실패하면 원인이 정렬이 아니었다 — **원래 오류를 그대로** 올린다(에러 문구를
 *     바꾸지 않는다). 오류 본문의 글자('invalid sort')를 읽어 판정하지 않는 이유도 이것이다:
 *     재시도 자체가 판정이라 서버 문구가 바뀌어도 답이 같다.
 */
export interface ListCustomerMasterResult extends ListCustomerMasterResponse {
  /**
   * 이 서버가 **거절해서 빼고 다시 받은** 정렬 값. null = 요청한 대로 정렬된 답이다.
   * 값이 있으면 목록 순서는 **서버 기본 순서**이고 화면이 고른 순서가 아니다.
   */
  rejectedSort: NonNullable<ListCustomerMasterQuery['sort']> | null;
  /**
   * 계획서 요구 2a §4(26-08-21) — 이 서버가 기존고객사 필터(former)를 거절해서 **빼고
   * 다시 받았는가**. true 면 이 목록은 기존고객사만이 아니다 — 화면이 그 사실을 말해야
   * 한다(조용히 걸러진 척하면 운영자가 전 고객사를 기존고객사로 읽는다). 판정 방식은
   * rejectedSort 와 같다: 재시도 자체가 판정이고 오류 문구를 읽지 않는다.
   */
  rejectedFormer: boolean;
}

export interface ListCustomerMasterQuery {
  // round8 (요구 1) — 국내/해외 분리. domestic=countryCode 'KR', overseas=그 외.
  scope?: 'domestic' | 'overseas';
  // round11 (요구 9) — 정렬. 미지정 = **서버 기본 'contractedAt'**(계약일 오름차순, 계약일을
  // 모르는 고객사는 맨 뒤 — 다계약 표시 차수3(26-08-05) 오너 선택. 서버가 scope 전체를
  // 정렬한 뒤 자르므로 페이지 경계까지 정확하다).
  // 'recent' = 등록 최근순 / 'name' = 가나다순(서버가 한 페이지 내에서 ko locale 재정렬).
  // ⚠ 정정5(26-08-06) — 'contractedAt' 는 **이 차수의 api 부터** 아는 값이다. 그 값을 모르는
  //   서버는 400 을 내고, list() 가 정렬을 빼고 한 번 다시 받아 그 사실을 rejectedSort 로
  //   말한다(ListCustomerMasterResult 주석에 근거). 새 값을 여기 더할 때도 같은 규칙이다.
  // 'latestContract' 는 26-08-06 api 부터 아는 값이다(기본 정렬 개편 — 최신 계약일 내림차순 +
  // 계약 체결중 맨 위). ⚠ 이 값을 보내려면 **api 가 먼저 떠 있어야** 한다 — 구버전 api 는
  // 400 을 낸다. 그때는 list() 의 400 폴백이 정렬을 빼고 다시 받아 시트를 띄운다.
  sort?: 'latestContract' | 'contractedAt' | 'recent' | 'name';
  search?: string;
  segment?: string;
  salesOwnerCode?: string;
  status?: CustomerMasterStatus;
  includeInactive?: boolean;
  // 이슈1e — 삭제된 고객사 포함(ADMIN 전용; 서버에서 역할 게이팅).
  includeDeleted?: boolean;
  // round10 (요구 5) — '종료'(retired) 고객사 포함 토글. 기본 숨김.
  includeRetired?: boolean;
  /**
   * 계획서 요구 2a §3·§4(26-08-21) — 기존고객사 필터. **상태 enum 이 아니라 별도
   * 파라미터다**(파생 분류라 status 에 넣지 않는다). true 면 서버가 세 조건(계약을 한 적
   * 있음 ∧ 진행(ACTIVE·DRAFT) 계약 0 ∧ 나가 있는 로봇 0)으로 거르고, 기본 종료 숨김을
   * **스스로 이긴다**(includeRetired 를 함께 보낼 필요가 없다). total 은 걸러낸 뒤 건수.
   * ⚠ 이 값은 **이 차수의 api 부터** 아는 파라미터다 — 구버전 api 는 400 을 내고,
   *   list() 가 이 값을 빼고 다시 받아 그 사실을 rejectedFormer 로 말한다(sort 와 같은 규칙).
   */
  former?: boolean;
  expiringWithinDays?: number;
  page?: number;
  pageSize?: number;
}

// docs/plan/customer-master-ux/plan.md §3 — 신규 등록 DTO (Phase 2)
//
// 계약 사실화 1단계 정정(26-07-28) — 줄 필드의 **3상태**(서버 DTO 와 같은 계약):
//   미전송(undefined) = 이 필드를 편집하지 않았다 → 기존 값 유지
//   빈 문자열('')     = 사용자가 칸을 비웠다     → 지우기
//   값                = 그 값으로 저장
// 그래서 편집 모드의 모달은 빈 칸을 **명시적으로 ''로** 보낸다. 종전엔 빈 칸을
// 미전송으로 보내 '지우기'가 조용히 무동작이 됐다.
export interface CreateCustomerMasterRobotLine {
  // 계약 사실화 1단계 정정(26-07-28) — 편집 시 이 입력이 가리키는 기존 줄의 id
  // (CustomerMasterRow.robots[].id). 신규 행은 없음. 서버는 이 id 로 줄을 찾고,
  // 없으면 종전 순서 규칙으로 폴백한다.
  lineId?: string;
  // 계약 사실화 1단계 정정4(26-07-29) — "이 화면은 줄 id 로 신원을 말한다"는 **선언**.
  // true 면 서버는 위 lineId 가 비어 있는 입력을 '새 행'으로 확정한다(순서 폴백 금지).
  //
  // ⚠ 모달은 **항상** true 로 보낸다 — 행이 전부 새 행이어서 lineId 가 하나도 없는
  //   저장에서도 마찬가지다. 이 플래그는 "이 행에 id 가 있다"가 아니라 "이 화면은 id 로
  //   신원을 말한다"는 뜻이기 때문이다. 종전(정정3)엔 서버가 payload 에 lineId 가
  //   하나라도 있는지로 이것을 **추론**했고, 새 행을 추가한 뒤 기존 행을 전부 지운
  //   저장에서 신호가 사라져 옛 클라이언트로 오판했다(새 행이 지워질 줄을 차지해 그
  //   줄의 계약 형태·방향·메모를 물려받았다).
  usesLineIds?: boolean;
  modelCode: string;
  quantity: number;
  // 계약 사실화 1단계 정정(26-07-28) — 줄별 계약 형태 **원문**(7값).
  // SALE / LEASE_OPERATING / LEASE_FINANCE / PILOT / DEMO / EVENT / INTERNAL.
  // 줄 칸에서 고른 값이 그대로 실린다. 아래 contractType(2값)보다 우선한다.
  reason?: string;
  // 계약 사실화 1단계(26-07-28) — 줄별 계약 형태('렌탈' / '판매', 접힌 2값).
  // 줄 칸을 '공통값 따름'으로 둔 줄에만 실린다(화면에 "(공통값: 렌탈)" 로 보이는
  // 명시적 상속을 모달이 확정해 보낸다). 7값 줄은 위 reason 으로 보내므로 이
  // 필드가 파일럿·시연을 렌탈로 덮는 일은 없다.
  contractType?: string;
  direction?: string;
  setting?: string;
  grillPlate?: string;
  specialParts?: string;
  wireGrill?: string;
  // 계약 사실화 1단계 정정(26-07-28) — '' = 비움. 도입료는 컬럼이 NOT NULL 이라
  // 서버가 0 으로, 판매가는 nullable 이라 null 로 저장한다.
  unitPriceKrw?: string | null; // BigInt as string
  listPriceKrw?: string | null;
  // S1 Phase 5 (R2 #3) — PDF §3 도입 로봇 모델 row 의 per-line 필드.
  // 기존 customer-level profileConfig(단일 Store ID) 대체.
  // S2 Phase 2.5 — + 희망 방향.
  // FB2 §4B (결정 G) — 희망 색상/불감지 create body 키 제거(모달에서 미입력).
  //   DB 컬럼 ContractLine.desiredColor/desiredSensor 와 서비스 쓰기는 보존.
  //   GET 응답(CustomerMasterRow.robots[])은 컬럼 유지.
  //
  // 계약 사실화 1단계 정정4(26-07-29) — 종전 주석의 "(서버가 null 기록)"은 줄 저장이
  // 전량 재생성에서 차이 반영(diff-update)으로 바뀐 뒤로 **기존 줄에는 거짓**이다.
  // 지금 서버 동작은 두 갈래다(customer-master.service.ts):
  //   • 새로 생기는 줄 — 미전송이면 null 로 기록(그 줄엔 원래 값이 없었다).
  //   • 이미 있는 줄 — 미전송 필드는 update 문에 아예 넣지 않는다 → **값 보존**.
  // 그래서 이 화면에서 저장해도 희망 색상/불감지는 지워지지 않고, 반대로 어느
  // 화면에서도 지울 수 없다(입력 칸이 없으니까). 그 판단 근거는 모달의 전송부
  // 주석에 적혀 있다(CustomerMasterCreateModal.tsx storeId/desiredDirection 위).
  storeId?: string;
  desiredDirection?: string;
}

export interface CreateCustomerMasterBody {
  // §1 고객사 정보
  displayName: string;
  legalName?: string;
  segment?: string;
  ownerEntity?: string;
  // round8 (요구 1) — 국내/해외. 2자리 국가코드. 미지정 시 서버가 'KR' 기본.
  countryCode?: string;
  // round11 (요구 10) — 쇼잉 여부. null = 클리어(미입력).
  isShowing?: boolean | null;
  addressLine?: string;
  addressMapUrl?: string;
  hasAJ?: boolean;
  // 운영자 피드백 (round 3) — '렌탈사' string. backend 가 hasAJ derive.
  rentalCompany?: string;
  hasInsurance?: boolean | null; // round5 #2b③ — null = 클리어(미정)
  notes?: string;
  // S1 Phase 5 (R2 #3) — PDF §1 '매장 수' (예상 매장 수). Customer.expectedStoreCount.
  expectedStoreCount?: number;
  // §2 담당자
  primaryContactName?: string;
  primaryContactPosition?: string;
  primaryContactPhone?: string;
  primaryContactEmail?: string;
  // §4 계약 (선택) — 영업 담당 포함 (PDF §4)
  salesOwnerCode: string;
  contractType?: string; // 렌탈 / 판매
  // round5 #2b① — contractNumber 제거(서버가 읽지 않던 죽은 필드; humanId 자동 발번).
  // round5 #2b③ — 편집 모드 클리어: 날짜는 null(서버가 null=클리어),
  // 문자열은 ''(서버 `|| null` 패턴이 클리어로 처리).
  contractedAt?: string | null;
  endDate?: string | null;
  rentalTermMonths?: number;
  depositKrw?: string | null;
  // 개선 9건 차수2(26-08-04) §7 — 지원금·소개비. 보증금과 같은 전송 규칙: 숫자 문자열
  // 또는 '' (서버 DTO 의 @BigIntWire 가 '' → null 로 접어 **지우기**로 처리). 키를
  // 아예 싣지 않으면 서버가 그 칸을 손대지 않는다(값 보존).
  subsidyKrw?: string | null;
  referralFeeKrw?: string | null;
  subscriptionStatus?: string;
  // §3 로봇 (필수, 1+)
  robots: CreateCustomerMasterRobotLine[];
  // §6 기타 정보
  grillSelectorType?: string;
  installScheduledAt?: string | null;
}

// Phase 3 — edit modal 의 PATCH body. Create 와 같은 모양이지만 robots
// 가 optional (없으면 lines 보존).
export interface UpdateCustomerMasterBody extends Partial<
  Omit<CreateCustomerMasterBody, 'robots' | 'salesOwnerCode'>
> {
  salesOwnerCode?: string;
  robots?: CreateCustomerMasterRobotLine[];
}

export const customerMasterApi = {
  /**
   * 목록 조회. **구버전 서버가 모르는 새 파라미터 때문에 시트가 통째로 비지 않게** 한다 —
   * 근거·갈림길은 ListCustomerMasterResult 주석에 있다.
   *
   * 계획서 요구 2a §4(26-08-21) — 거절될 수 있는 파라미터가 둘이 됐다(sort · former).
   * 400 을 만나면 **가장 새 파라미터부터 하나씩 빼며** 다시 받는다(former → sort → 둘 다).
   * former 를 먼저 빼는 이유: former 를 모르는 서버도 sort('latestContract')는 알 수 있다
   * (former 가 더 나중에 생긴 값이다). 어느 갈래든 재시도가 성공하면 그 뺀 값이 원인이었다는
   * 뜻이므로 rejected* 로 말하고, 전부 실패하면 원인이 이 파라미터들이 아니었다 — **원래
   * 오류를 그대로** 올린다(두 번째 오류로 덮지 않는다). 오류 본문의 글자를 읽어 판정하지
   * 않는 이유도 종전과 같다: 재시도 자체가 판정이라 서버 문구가 바뀌어도 답이 같다.
   */
  async list(query: ListCustomerMasterQuery = {}): Promise<ListCustomerMasterResult> {
    const fetchOnce = async (params: ListCustomerMasterQuery) => {
      const { data } = await apiClient.get<ListCustomerMasterResponse>('/customer-master', {
        params,
      });
      return data;
    };
    try {
      return { ...(await fetchOnce(query)), rejectedSort: null, rejectedFormer: false };
    } catch (err) {
      // 400 이 아니면 이 폴백의 대상이 아니다(권한·서버 오류를 '파라미터 문제'로 뭉개면
      // 화면이 진짜 원인을 말하지 못한다).
      if (!axios.isAxiosError(err) || err.response?.status !== 400) throw err;
      const hasSort = query.sort !== undefined;
      const hasFormer = query.former !== undefined;
      // 새 파라미터를 아예 안 실은 조회는 재시도할 것이 없다 — 400 이 그대로 올라간다.
      if (!hasSort && !hasFormer) throw err;
      const { sort: _sort, former: _former, ...base } = query;
      // ① 기존고객사 필터만 빼고.
      if (hasFormer) {
        try {
          const data = await fetchOnce(hasSort ? { ...base, sort: query.sort } : base);
          return { ...data, rejectedSort: null, rejectedFormer: true };
        } catch {
          // former 만의 문제가 아니었다 — 아래 갈래로.
        }
      }
      // ② 정렬만 빼고(운영자가 건 다른 필터는 그대로 간다 — former 포함).
      if (hasSort) {
        try {
          const data = await fetchOnce(hasFormer ? { ...base, former: query.former } : base);
          return { ...data, rejectedSort: query.sort ?? null, rejectedFormer: false };
        } catch {
          // sort 만의 문제도 아니었다 — 아래 갈래로.
        }
      }
      // ③ 둘 다 빼고 — 둘 다 모르는 아주 오래된 서버.
      if (hasSort && hasFormer) {
        try {
          const data = await fetchOnce(base);
          return { ...data, rejectedSort: query.sort ?? null, rejectedFormer: true };
        } catch {
          // 이 파라미터들 때문이 아니었다 — 원래 오류로.
        }
      }
      throw err;
    }
  },
  // PDF §Phase B followup — 단일 row fetch. CustomerDetailPage 의
  // CustomerMasterSpecSection 이 200건 list 대신 본 endpoint 사용.
  async get(id: string): Promise<CustomerMasterRow> {
    const { data } = await apiClient.get<CustomerMasterRow>(`/customer-master/${id}`);
    return data;
  },
  async create(body: CreateCustomerMasterBody): Promise<CustomerMasterRow> {
    const { data } = await apiClient.post<CustomerMasterRow>('/customer-master', body);
    return data;
  },
  async update(id: string, body: UpdateCustomerMasterBody): Promise<CustomerMasterRow> {
    const { data } = await apiClient.patch<CustomerMasterRow>(`/customer-master/${id}`, body);
    return data;
  },
  // PDF §Phase B 페이지 2 §3 — 진행 상태 인라인 편집.
  // status=null → manual override 해제, 자동 계산 복원.
  // FB §4b — status='return_planned' 시 reason(회수 사유) 동반 전송.
  async updateStatus(
    id: string,
    status: CustomerMasterStatus | null,
    reason?: string,
  ): Promise<CustomerMasterRow> {
    const { data } = await apiClient.patch<CustomerMasterRow>(`/customer-master/${id}/status`, {
      status,
      ...(reason !== undefined ? { reason } : {}),
    });
    return data;
  },
  // S1 Phase 7 (R2 #6) — '할당 요청' 버튼. 제조팀에 ROBOT_ASSIGNMENT_REQUESTED
  // 알람 재발송.
  async requestRobotAssignment(id: string): Promise<{ status: string }> {
    const { data } = await apiClient.post<{ status: string }>(
      `/customer-master/${id}/request-robot-assignment`,
    );
    return data;
  },
};
